import { useMemo } from 'react';
import { Trans } from 'react-i18next';
import { t } from 'i18next';
import SyncIcon from '@mui/icons-material/Sync';

import {
  ColumnProps,
  Page,
  PageHeaderAction,
  Table,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import { stringify } from 'utils/base64';
import { getPageName } from 'utils/format';
import { getFieldDataAndAccessor } from 'utils/useFieldDataAndAccessor';
import { formatDate, DATE_TIME_FORMAT_WITH_SECONDS } from 'utils/dates';
import { RouteNames } from 'config/routeNames';
import { CommentCodesTransferModel, CommentCodesTransferStatus, FilterOptionModel } from 'models';
import LeoSelectField from 'components/input-fields/leoSelectField/LeoSelectField';
import {
  useCommentCodesTransferList,
  useSyncCommentCodesMutation,
} from 'features/commentCodesTransfer';
import { SortDirection } from 'utils/arrays';

export const CommentCodeTransferPage = () => {
  const {
    onPageChange,
    onRowsPerPageChange,
    onSort,
    onFilter,
    onLoadQuery,
    params,
    apiParams,
    onTableColumnVisibilityChange,
  } = useTableQueryParams({
    order: stringify([{ orderBy: 'id', order: SortDirection.DESC }]),
  });

  const {
    data: listData,
    isLoading,
    isFetching,
    dataUpdatedAt,
  } = useCommentCodesTransferList(apiParams);

  const { mutateAsync: syncCommentCodesMutation, isPending: isSyncMutationPending } =
    useSyncCommentCodesMutation();

  const { data: statusData, accessor: statusAccessor } = getFieldDataAndAccessor(
    Object.values(CommentCodesTransferStatus).map((status) => ({
      value: status,
      label: t(`LEO_MEGJEGYZES_KODOK_ATVETEL.STATUS_VALUES.${status}`),
    })),
    'value',
    'label'
  );

  const filters: FilterOptionModel[] = [
    {
      headerTitle: 'LEO_MEGJEGYZES_KODOK_ATVETEL.STATUS',
      component: (
        <LeoSelectField
          name="status"
          data={statusData}
          accessor={statusAccessor}
          label="LEO_MEGJEGYZES_KODOK_ATVETEL.STATUS"
        />
      ),
      name: 'status',
      panelFieldWidth: 12,
      abbrName: t('FILTERABBR.STATUS'),
    },
  ];

  const { activePanel, closePanel, setPanel } = usePanelState();

  const filterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.LEO_MEGJEGYZES_KODOK_ATVETEL),
    isFetching,
  });

  const panels = [filterConfig];

  const columns: ColumnProps<CommentCodesTransferModel>[] = [
    {
      key: 'jobId',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK_ATVETEL.ID" />,
      accessor: 'jobId',
      sortable: true,
    },
    {
      key: 'startedAt',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK_ATVETEL.START_TIME" />,
      accessor: ({ startedAt }) => formatDate(startedAt, DATE_TIME_FORMAT_WITH_SECONDS),
      field: 'startedAt',
      sortable: true,
    },
    {
      key: 'finishedAt',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK_ATVETEL.END_TIME" />,
      accessor: ({ finishedAt }) => formatDate(finishedAt, DATE_TIME_FORMAT_WITH_SECONDS),
      field: 'finishedAt',
      sortable: true,
    },
    {
      key: 'status',
      header: <Trans i18nKey="LEO_MEGJEGYZES_KODOK_ATVETEL.STATUS" />,
      accessor: ({ status }) => statusAccessor[status],
      field: 'status',
      sortable: true,
    },
  ];

  const headerActions = useMemo<PageHeaderAction[]>(
    () => [
      {
        name: 'sync',
        label: 'LEO_MEGJEGYZES_KODOK_ATVETEL.SYNC_BUTTON',
        icon: <SyncIcon fill="currentColor" />,
        onClick: () => syncCommentCodesMutation(),
        isLoading: isSyncMutationPending,
      },
    ],
    [isSyncMutationPending, syncCommentCodesMutation]
  );

  return (
    <Page
      title={<Trans i18nKey="LEO_MEGJEGYZES_KODOK_ATVETEL.TITLE" />}
      inScroll
      activePanel={activePanel}
      closePanel={closePanel}
      setPanel={setPanel}
      panels={panels}
    >
      <Table
        filterDetailsData={{ backgroundJobStatusAccessor: statusAccessor }}
        panels={panels}
        setPanel={setPanel}
        activePanel={activePanel}
        timestamp={dataUpdatedAt}
        list={listData?.data || []}
        total={listData?.meta?.total}
        loading={isLoading}
        params={params}
        onSort={onSort}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        columns={columns}
        pageHeaderActions={headerActions}
        onTableColumnVisibilityChange={onTableColumnVisibilityChange}
        onLoadQuery={onLoadQuery}
        keyField="id"
      />
    </Page>
  );
};
